'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Clock, Zap, Shield, ChevronRight } from 'lucide-react';
import { QuizQuestion, QuizAttempt, QuizSession } from './QuizArenaContent';

interface QuizPlayerProps {
  quizId: string;
  title: string;
  category: string;
  questions: QuizQuestion[];
  onComplete: (session: QuizSession) => void;
  onExit: () => void;
}

type AnswerState = 'idle' | 'correct' | 'wrong' | 'timeout';

const OPTION_LABELS = ['A', 'B', 'C', 'D'];

export default function QuizPlayer({ quizId, title, category, questions, onComplete, onExit }: QuizPlayerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [answerState, setAnswerState] = useState<AnswerState>('idle');
  const [timeLeft, setTimeLeft] = useState(questions[0]?.timeLimit ?? 20);
  const [attempts, setAttempts] = useState<QuizAttempt[]>([]);
  const [totalScore, setTotalScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [streak, setStreak] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const answeredRef = useRef(false);

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex) / questions.length) * 100;

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleAnswer = useCallback((optionIndex: number | null, isTimeout = false) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    stopTimer();

    const timeTaken = Math.round((Date.now() - questionStartTime) / 1000);
    const isCorrect = optionIndex === currentQuestion.correctIndex;
    const speedBonus = isCorrect && timeTaken <= 5 ? 50 : isCorrect && timeTaken <= 10 ? 25 : 0;
    const pointsEarned = isCorrect ? currentQuestion.points + speedBonus : 0;

    setSelectedOption(optionIndex);
    setAnswerState(isTimeout ? 'timeout' : isCorrect ? 'correct' : 'wrong');
    setShowExplanation(true);

    if (isCorrect) {
      setStreak(s => s + 1);
      setTotalScore(s => s + pointsEarned);
    } else {
      setStreak(0);
    }

    const attempt: QuizAttempt = {
      questionId: currentQuestion.id,
      selectedIndex: optionIndex,
      isCorrect,
      timeTaken,
      pointsEarned,
    };

    setAttempts(prev => [...prev, attempt]);
  }, [currentQuestion, questionStartTime, stopTimer]);

  // Timer
  useEffect(() => {
    answeredRef.current = false;
    setTimeLeft(currentQuestion.timeLimit);
    setQuestionStartTime(Date.now());
    setSelectedOption(null);
    setAnswerState('idle');
    setShowExplanation(false);

    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          handleAnswer(null, true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => stopTimer();
  }, [currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = () => {
    if (currentIndex + 1 >= questions.length) {
      const session: QuizSession = {
        quizId,
        title,
        category,
        totalQuestions: questions.length,
        attempts: [...attempts],
        totalScore,
        totalTime: attempts.reduce((s, a) => s + a.timeTaken, 0),
        completedAt: new Date().toISOString(),
      };
      onComplete(session);
    } else {
      setCurrentIndex(i => i + 1);
    }
  };

  const timerPercent = (timeLeft / currentQuestion.timeLimit) * 100;
  const timerColor = timerPercent > 50 ? '#10B981' : timerPercent > 25 ? '#F59E0B' : '#F43F5E';

  const getOptionStyle = (index: number): React.CSSProperties => {
    if (answerState === 'idle') {
      return {
        background: 'var(--muted)',
        border: '1.5px solid var(--border)',
        color: 'var(--foreground)',
        cursor: 'pointer',
      };
    }
    if (index === currentQuestion.correctIndex) {
      return {
        background: 'rgba(16,185,129,0.15)',
        border: '1.5px solid #10B981',
        color: '#10B981',
        cursor: 'default',
      };
    }
    if (index === selectedOption && !answerState.includes('correct')) {
      return {
        background: 'rgba(244,63,94,0.15)',
        border: '1.5px solid #F43F5E',
        color: '#F43F5E',
        cursor: 'default',
      };
    }
    return {
      background: 'var(--muted)',
      border: '1.5px solid var(--border)',
      color: 'var(--muted-foreground)',
      opacity: 0.5,
      cursor: 'default',
    };
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--background)' }}>
      {/* Top Bar */}
      <div className="sticky top-0 z-10 px-4 py-3 flex items-center gap-4"
        style={{ background: 'var(--card)', borderBottom: '1px solid var(--border)' }}>
        <button onClick={onExit} className="p-1.5 rounded-lg transition-all hover:opacity-70"
          style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
          <X size={16} />
        </button>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>
              Question {currentIndex + 1} of {questions.length}
            </span>
            <div className="flex items-center gap-3">
              {streak >= 2 && (
                <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
                  🔥 {streak} streak
                </span>
              )}
              <span className="flex items-center gap-1 text-sm font-extrabold" style={{ color: '#F59E0B' }}>
                <Zap size={13} style={{ fill: '#F59E0B' }} />
                {totalScore}
              </span>
            </div>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #7C3AED, #06B6D4)' }} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-start px-4 py-6 max-w-2xl mx-auto w-full">
        {/* Timer */}
        <div className="w-full mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-1.5">
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{ background: 'rgba(124,58,237,0.15)', color: '#A78BFA' }}>
                {currentQuestion.category}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                style={{
                  background: currentQuestion.difficulty === 'Easy' ? 'rgba(16,185,129,0.1)' : currentQuestion.difficulty === 'Medium' ? 'rgba(245,158,11,0.1)' : 'rgba(244,63,94,0.1)',
                  color: currentQuestion.difficulty === 'Easy' ? '#10B981' : currentQuestion.difficulty === 'Medium' ? '#F59E0B' : '#F43F5E',
                }}>
                {currentQuestion.difficulty}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock size={13} style={{ color: timerColor }} />
              <span className="text-sm font-extrabold tabular-nums" style={{ color: timerColor, minWidth: '24px' }}>
                {timeLeft}s
              </span>
            </div>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
            <div className="h-full rounded-full transition-all duration-1000 linear"
              style={{ width: `${timerPercent}%`, background: timerColor }} />
          </div>
        </div>

        {/* Question */}
        <div className="w-full rounded-2xl p-6 mb-5"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
          <p className="text-lg font-bold text-foreground leading-relaxed">{currentQuestion.question}</p>
          <p className="text-xs mt-2" style={{ color: 'var(--muted-foreground)' }}>
            Worth <span className="font-bold" style={{ color: '#F59E0B' }}>{currentQuestion.points} pts</span>
            {answerState === 'idle' && <span> · Speed bonus up to +50 pts</span>}
          </p>
        </div>

        {/* Options */}
        <div className="w-full space-y-3 mb-5">
          {currentQuestion.options.map((option, index) => (
            <button
              key={index}
              onClick={() => answerState === 'idle' && handleAnswer(index)}
              disabled={answerState !== 'idle'}
              className="w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-200 hover:scale-[1.01]"
              style={getOptionStyle(index)}
            >
              <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-extrabold flex-shrink-0"
                style={{
                  background: answerState !== 'idle' && index === currentQuestion.correctIndex ?'#10B981'
                    : answerState !== 'idle'&& index === selectedOption ?'#F43F5E' :'var(--card)',
                  color: answerState !== 'idle' && (index === currentQuestion.correctIndex || index === selectedOption)
                    ? '#fff' :'inherit',
                }}>
                {OPTION_LABELS[index]}
              </span>
              <span className="font-medium text-sm leading-snug">{option}</span>
              {answerState !== 'idle' && index === currentQuestion.correctIndex && (
                <span className="ml-auto text-xs font-bold" style={{ color: '#10B981' }}>✓ Correct</span>
              )}
            </button>
          ))}
        </div>

        {/* Feedback */}
        {showExplanation && (
          <div className="w-full rounded-xl p-4 mb-5"
            style={{
              background: answerState === 'correct' ? 'rgba(16,185,129,0.08)' : 'rgba(244,63,94,0.08)',
              border: `1px solid ${answerState === 'correct' ? '#10B981' : '#F43F5E'}33`,
            }}>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-base"
                style={{ background: answerState === 'correct' ? 'rgba(16,185,129,0.2)' : 'rgba(244,63,94,0.2)' }}>
                {answerState === 'correct' ? '✅' : answerState === 'timeout' ? '⏰' : '❌'}
              </div>
              <div>
                <p className="text-sm font-bold mb-1"
                  style={{ color: answerState === 'correct' ? '#10B981' : '#F43F5E' }}>
                  {answerState === 'correct' ? `+${attempts[attempts.length - 1]?.pointsEarned ?? 0} points earned!` : answerState === 'timeout' ? 'Time\'s up!' : 'Incorrect'}
                </p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  {currentQuestion.explanation}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Next Button */}
        {showExplanation && (
          <button onClick={handleNext}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>
            {currentIndex + 1 >= questions.length ? (
              <><Shield size={15} /> See Results</>
            ) : (
              <>Next Question <ChevronRight size={15} /></>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
